import {
  ApiError,
  getErrorMessage,
  NetworkError,
  TimeoutError,
} from "./api-errors";

function getConfiguredApiUrl() {
  return (
    process.env.REACT_APP_API_URL ??
    process.env.NEXT_PUBLIC_API_URL ??
    process.env.VITE_API_URL ??
    process.env.API_URL
  );
}

function inferIsProductionVariant() {
  const variant =
    process.env.EXPO_PUBLIC_APP_VARIANT ??
    process.env.REACT_APP_APP_VARIANT ??
    process.env.APP_VARIANT;

  if (variant) {
    return variant === "production";
  }

  return process.env.NODE_ENV === "production";
}

export const prodApiUrl = "https://api.harper.new/api";
export const devApiUrl = "http://localhost:8080/api";
export const API_VERSION =
  process.env.REACT_APP_API_VERSION ??
  process.env.NEXT_PUBLIC_API_VERSION ??
  "v1";

function normalizeBaseUrl(url: string) {
  return url.replace(/\/+$/, "");
}

function appendPathSegment(baseUrl: string, segment?: string) {
  const normalizedSegment = segment?.replace(/^\/+/, "");

  if (!normalizedSegment) {
    return normalizeBaseUrl(baseUrl);
  }

  return `${normalizeBaseUrl(baseUrl)}/${normalizedSegment}`;
}

export function resolveBaseApiUrl(): string {
  let baseApiUrl = getConfiguredApiUrl();

  // Fallback to fixed URLs if the config isn't working
  if (!baseApiUrl) {
    console.warn("API URL not found in config, using fallback URL");

    const isProd = inferIsProductionVariant();
    baseApiUrl = isProd ? prodApiUrl : devApiUrl;
  }

  const normalizedBase = normalizeBaseUrl(baseApiUrl);
  const normalizedVersion = API_VERSION.replace(/^\/+/, "");

  if (normalizedVersion && normalizedBase.endsWith(`/${normalizedVersion}`)) {
    return normalizedBase;
  }

  return appendPathSegment(normalizedBase, normalizedVersion);
}

export const baseApi = resolveBaseApiUrl();

export type RequestOptions = {
  body?: any;
  method: string;
};

export type HeaderOptions = {
  method: string;
  headers: {
    "Content-Type"?: string;
    access_token?: string;
    refresh_token?: string;
    userId?: string;
  };
  body?: any;
};

async function makeRequest(
  slug: string,
  requestOptions: HeaderOptions,
  timeoutMs: number = 30000
) {
  const baseApiUrl = resolveBaseApiUrl();
  const fullUrl = appendPathSegment(baseApiUrl, slug);

  try {
    // Create an AbortController for the timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      // Sanitize headers to satisfy HeadersInit (remove undefined values)
      const sanitizedHeaders: Record<string, string> = Object.fromEntries(
        Object.entries(requestOptions.headers || {}).filter(
          ([, v]) => typeof v === "string"
        ) as Array<[string, string]>
      );

      const { headers: _headers, ...restRequestOpts } = requestOptions;

      const response = await fetch(fullUrl, {
        ...restRequestOpts,
        headers: sanitizedHeaders,
        signal: controller.signal,
      });

      // Clear the timeout since the request completed
      clearTimeout(timeoutId);

      if (!response.ok) {
        // Handle specific HTTP error codes
        throw await ApiError.fromResponse(response);
      }

      // Check if response is HTML (error page) instead of JSON
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("text/html")) {
        const text = await response.text();

        throw new ApiError(
          500,
          "Server Error",
          `${fullUrl}: Server returned HTML instead of JSON. This usually means the endpoint is incorrect or the server is down. ${text}`
        );
      }

      const res = await response.json();

      if (res?.error) {
        throw new ApiError(
          500,
          "Server Error",
          typeof res.error === "string" ? res.error : JSON.stringify(res.error)
        );
      }

      return res;
    } catch (error: any) {
      // Handle AbortController timeout
      if (error.name === "AbortError") {
        throw new TimeoutError(
          `Request to ${slug} timed out after ${timeoutMs}ms`
        );
      }
      throw error;
    }
  } catch (error: any) {
    const errorWithType: ApiError = error;
    // Handle network errors (offline, DNS failures, etc.)
    if (
      errorWithType.data?.message === "Network request failed" ||
      (errorWithType.data?.message &&
        errorWithType.data.message.includes("network")) ||
      (errorWithType.data?.message &&
        errorWithType.data.message.includes("fetch"))
    ) {
      throw new NetworkError(
        `Network error while fetching ${slug}: ${error.message}`
      );
    }

    // Log and rethrow the error
    console.error("Error fetching data:", error, fullUrl, slug);
    throw error;
  }
}

export async function fetchWithEnvelope<T>(
  slug: string,
  requestOptions: HeaderOptions,
  timeoutMs: number = 30000
): Promise<T> {
  return makeRequest(slug, requestOptions, timeoutMs) as Promise<T>;
}

/*
The companyId is sent with all authenticated requests. This is because most db queries will filter by the companyId.
*/

const supabaseId = process.env.REACT_APP_SUPABASE_PROJECT_ID;

export function setHeaderOptions({
  body,
  method,
}: RequestOptions): HeaderOptions {
  const sbTokenKey = `sb-${supabaseId}-auth-token`;

  const values = localStorage.getItem(sbTokenKey);

  if (!values) {
    throw new Error("No auth token found");
  }

  const { access_token, refresh_token, user } = JSON.parse(values || "{}");

  const userId = user?.id;

  const request = {
    method,
    headers: {
      "Content-Type": "application/json",
      [access_token ? "access_token" : ""]: access_token,
      [refresh_token ? "refresh_token" : ""]: refresh_token,
      userId,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };

  return request;
}

export async function setFileUploadOptions({
  body,
  method,
}: RequestOptions): Promise<HeaderOptions> {
  const sbTokenKey = `sb-${supabaseId}-auth-token`;

  const values = localStorage.getItem(sbTokenKey);

  if (!values) {
    throw new Error("No auth token found");
  }

  const { access_token, refresh_token } = JSON.parse(values || "{}");

  const request = {
    method,
    headers: {
      [access_token ? "access_token" : ""]: access_token,
      [refresh_token ? "refresh_token" : ""]: refresh_token,
    },
    body: body || undefined,
  };

  return request;
}

export async function fetchData<T>(
  url: string,
  requestOptions: RequestInit,
  timeoutMs: number = 30000
): Promise<T> {
  try {
    const isAbsolute = /^https?:\/\//.test(url);
    const baseApiUrl = resolveBaseApiUrl();
    const fullUrl = isAbsolute ? url : appendPathSegment(baseApiUrl, url);
    const hasSignal = Boolean(requestOptions.signal);
    const controller =
      !hasSignal && typeof AbortController !== "undefined"
        ? new AbortController()
        : null;
    const timeoutId =
      controller !== null
        ? setTimeout(() => controller.abort(), timeoutMs)
        : null;
    const response = await fetch(fullUrl, {
      ...requestOptions,
      ...(controller ? { signal: controller.signal } : {}),
    });

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    let res: any = null;
    try {
      res = await response.json();
    } catch (parseError) {
      res = null;
    }

    const extractErrorMessage = () => {
      if (!res) return undefined;
      if (typeof res.error === "string") {
        return res.error;
      }
      if (res.error?.message) {
        return res.error.message;
      }
      if (typeof res.message === "string" && res.message.length > 0) {
        return res.message;
      }
      if (res.error) {
        try {
          return JSON.stringify(res.error);
        } catch {
          return String(res.error);
        }
      }
      return undefined;
    };

    if (!response.ok) {
      const errorMessage =
        extractErrorMessage() || response.statusText || "Unknown error occurred.";

      throw new ApiError(
        response.status,
        response.statusText,
        errorMessage,
        res ?? undefined
      );
    }

    if (res?.error) {
      const errorMessage =
        extractErrorMessage() || "Unknown error occurred while processing response.";
      throw new ApiError(response.status, response.statusText, errorMessage, res);
    }

    return (res?.data ?? res) as T;
  } catch (error: any) {
    // Handle timeout errors from AbortSignal.timeout()
    if (
      error instanceof Error &&
      (error.name === "AbortError" || error.name === "TimeoutError")
    ) {
      const timeoutError = new TimeoutError(
        `Request timed out after ${timeoutMs}ms`
      );
      console.error(`Timeout Error: ${timeoutError.message}`);
      throw timeoutError;
    }

    if (error instanceof ApiError) {
      console.error(`API Error (${error.status}): ${error.message}`);
    } else if (error instanceof NetworkError) {
      console.error(`Network Error: ${error.message}`);
    } else if (error instanceof TimeoutError) {
      console.error(`Timeout Error: ${error.message}`);
    } else {
      console.error("Error in fetchData:", getErrorMessage(error));
    }

    // Rethrow to be handled by the caller
    throw error;
  }
}
